import { Box } from '@mui/material';
import { btnSize, btnType, Button } from '../../../../packages/ui/atoms/button';
import MedicationCard from '../../../../packages/ui/molecules/medication-card';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Markdown from 'markdown-to-jsx';
import { getAuth } from '../../../../redux/modules/auth/selectors';
import { getProfile } from '../../../../redux/modules/profile/selectors';
import { DomainTypesEnum } from '../../../../constants/CommonConstants';
import Header from '../../../../components/v2/Header';
import Typography from '../../../../components/v2/Typography';
import { typography } from '../../../../components/v2/Typography/index.constant';
import { PrioritiesCard } from '../../../provider/sessions/priority-list/PriorityList.constants';
import { getAssignedPrioritiesV2 } from '../../../../services/priority/priority.service';
import { getDomainGroup, updateAssociatedTag } from '../../../../services/member/member.service';
import { PriorityDetailDrawer } from '../../../provider/priorities/PriorityDetailDrawer';
import AddNewReportMedications from '../add-new-report/AddNewReportMedications';
import AddFactorDrawer, { DrawerType } from '../add-factor-drawer';
import { FactorType } from '../add-factor-drawer/FactorItem';
import AddExternalRxDrawer from './AddExternalRxDrawer';
import { MEDICATION_MODE } from '../add-new-report/AddNewReport.constants';
import { sxStyles, useStyles } from './index.styles';
import { getPreferredPharmacyDetails } from '../../../../services/conversation/conversation.service';
import { getFilters, DOMAIN_ELEMENT_VARIENT } from '../member-detail.constents';
import { ProfileInfoRole } from '../../../../components/v2/ProfileInfo';
import { profileActionCreators } from '../../../../redux/modules/profile';
import { mapToLegacy } from '../../../../utils/CommonUtils';
import {
  flattenExternalRxRecords,
  isExternalRxRecord,
  sortMedicationsWithExternalLast,
} from '../../../../utils/medicationUtils';
import {
  fetchNavItemStatus,
  initiateMdToolboxAutomation,
} from '../../../../services/datalab/datalab.service';
import { showSnackbar } from '../../../../redux/modules/snackbar';
import { colors, fontWeight, Icons, Text, textLevel } from '../../../../packages';
import DiscontinueRXDrawer from './DiscontinueRXDrawer';
import EditPharmacyDrawer from './EditPharmacyDrawer';
import dayjs from '../../../../utils/dayjs';
import EmptyState from '../../../provider/member-detail/components/EmptyState';

const Medications = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { memberId } = useParams();
  const { meta, isAdmin } = useSelector(getAuth);
  const { profile, demographicDetails } = useSelector(getProfile);
  const typeName = PrioritiesCard.Medications;
  const [isLoading, setIsLoading] = useState(false);
  const [domainOptions, setDomainOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [isDiscontinueDrawerOpen, setIsDiscontinueDrawerOpen] = useState(false);
  const [selectedMedicationForDiscontinue, setSelectedMedicationForDiscontinue] = useState(null);
  const [isEditPharmacyDrawerOpen, setIsEditPharmacyDrawerOpen] = useState(false);
  const [value, setValue] = useState({
    search: {
      searchKey: '',
    },
    pagination: {
      currentPage: 1,
      rowsPerPage: 10,
    },
    sorter: {
      direction: 'asc',
      column: '',
    },
  });
  const [selectedType, setSelectedType] = useState(null);
  const [selectedItem, setSelectedItem] = useState({});
  const [reset, setReset] = useState(false);
  const { lookupData, domainTypes } = useSelector(getProfile);
  const [shouldStartAutomation, setShouldStartAutomation] = useState(false);
  const [isAutomationLoading, setIsAutomationLoading] = useState(false);
  const [automationSessionId, setAutomationSessionId] = useState(null);
  const [isSavingMedication, setIsSavingMedication] = useState(false);
  const [displayedRecords, setDisplayedRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pharmacyDetails, setPharmacyDetails] = useState(null);
  const [isLoadingPharmacy, setIsLoadingPharmacy] = useState(false);
  const [showMedicationSelection, setShowMedicationSelection] = useState(false);
  const [showExternalRx, setShowExternalRx] = useState(false);
  const [existingExternalRx, setExistingExternalRx] = useState(null);
  const [medicationMode, setMedicationMode] = useState(MEDICATION_MODE.ADD);
  const [existingMedicationForDrawer, setExistingMedicationForDrawer] = useState(null);

  useEffect(() => {
    // Don't fetch data while medication is being saved to prevent UI refresh
    if (isSavingMedication) {
      return;
    }

    setIsLoading(true);
    getAssignedPrioritiesV2({
      patientId: memberId,
      dataDomainType: typeName,
      pageNumber: value.pagination.currentPage - 1,
      pageSize: value.pagination.rowsPerPage,
      importanceLevels: getFilters(value)?.importanceLevels,
      searchQuery: value.search.searchKey ?? '',
    })
      .then(({ data }) => {
        setTotalRecords(data?.totalRecords || 0);

        if (value.pagination.currentPage === 1) {
          setDisplayedRecords(data?.records || []);
        } else {
          setDisplayedRecords(prev => [...prev, ...(data?.records || [])]);
        }
        setIsLoading(false);
      })
      .catch(e => {
        console.error(e);
        setIsLoading(false);
      });
  }, [value, reset, isSavingMedication]);

  const fetchPharmacyDetails = async () => {
    const preferredPharmacy = demographicDetails?.preferredPharmacy;
    if (preferredPharmacy) {
      setIsLoadingPharmacy(true);
      try {
        const { data } = await getPreferredPharmacyDetails({ pharmacyId: preferredPharmacy });
        setPharmacyDetails(data);
        setIsLoadingPharmacy(false);
      } catch (error) {
        console.error('Failed to fetch pharmacy details:', error);
        setIsLoadingPharmacy(false);
      }
    }
  };

  useEffect(() => {
    fetchPharmacyDetails();
  }, [demographicDetails?.preferredPharmacy]);

  const fetchDomainForm = typeId => {
    getDomainGroup({ domainTypeId: typeId })
      .then(({ data }) => {
        const opts = data.map(item => ({
          label: item.name,
          groupId: item.Id,
          items: item.relatedElements.map(element => ({ label: element.name, value: element.Id })),
          relatedElements: item.relatedElements,
        }));
        setDomainOptions(opts);
        setOptionsLoading(false);
      })
      .catch(() => {
        setDomainOptions([]);
        setOptionsLoading(false);
      });
  };

  // Find the "Other" option ID for external RX
  const otherDomainElementId = useMemo(() => {
    for (const group of domainOptions) {
      const otherItem = group.items.find(item => item.label.toLowerCase() === 'other');
      if (otherItem) {
        return otherItem.value;
      }
    }
    return null;
  }, [domainOptions]);

  // Flatten external RX records (Other) - group by pharmacyNotes (unique ID)
  // Records with same pharmacyNotes are the same external RX (main + update history)
  const flattenedRecords = useMemo(() => {
    return flattenExternalRxRecords(displayedRecords);
  }, [displayedRecords]);

  useEffect(() => {
    const domain = domainTypes.find(item => item.typeName === DomainTypesEnum.MEDICATIONS);
    if (domain) {
      setOptionsLoading(true);
      fetchDomainForm(domain.typeId);
    }
  }, [selectedType, domainTypes]);

  const onCloseDrawer = (shouldClose = true) => {
    if (shouldClose) {
      setSelectedType(null);
    }
    setReset(!reset);
    setShouldStartAutomation(false);
    setIsSavingMedication(false);
    setIsAutomationLoading(false);
    setAutomationSessionId(null);
    setShowMedicationSelection(false);
    setShowExternalRx(false);

    setValue(prev => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        currentPage: 1,
      },
    }));

    // Fetch member details when closing drawer to avoid glitchy behavior
    dispatch(profileActionCreators.fetchDemographicMetadata());
    dispatch(profileActionCreators.fetchDemographicDetails(memberId));
  };

  const onAutomationError = () => {
    // Reset loading states but keep drawer open
    setIsSavingMedication(false);
    setIsAutomationLoading(false);
    setShouldStartAutomation(false);
    setAutomationSessionId(null);
  };

  const startMdToolboxAutomation = async (patientId, associatedTagId, confidantProviderId) => {
    setIsAutomationLoading(true);
    try {
      const response = await initiateMdToolboxAutomation(
        {
          patientId,
          associatedTagId,
          providerId: confidantProviderId,
        },
        meta.userId,
      );

      if (!response.data?.success) {
        const errorMessage =
          response.data?.error?.message ||
          response.data?.message ||
          'Failed to submit the medication data. Please try again.';
        throw new Error(errorMessage);
      }

      const sessionId = response.data?.sessionId;

      if (!sessionId) {
        console.warn('No sessionId found in automation response');
      }
      setIsAutomationLoading(false);
      return { ...response, sessionId, isQueued: response.data?.isQueued };
    } catch (error) {
      setIsAutomationLoading(false);
      const errorMessage =
        error.data?.error?.message ||
        error.data?.message ||
        'Failed to submit the medication data. Please try again.';

      throw new Error(errorMessage);
    }
  };

  const onSubmitNewReport = (payload, handleQueued) => {
    setIsSavingMedication(true);

    const data = {
      patientId: memberId,
      tagItems: payload.medications.map(item => ({
        assignedBy: profile.fullName || meta.nickName,
        domainElementId: item.item.value,
        metaData: {
          notes: item?.additionalNotes,
          selectedImportance: item?.status,
          rxDrugInfo: {
            sig: item?.sig,
            externalMedicationId: item?.externalMedicationId,
            externalMedicationName: item?.externalMedicationName,
            refills: item?.refills,
            dispenseAmount: item?.dispenseAmount ? Number(item.dispenseAmount) : 0,
            associatedDiagnoses: item?.associatedDiagnoses,
            allowSubstitutions: item?.allowSubstitutions,
            pharmacyNotes: item?.pharmacyNotes,
            additionalNotes: item?.additionalNotes,
            quantity: item.quantity ? Number(item.quantity) : 0,
            dose: item.dose ?? '',
            frequency: item?.frequency?.value ?? item?.frequency,
            duration: item?.duration ? Number(item.duration) : 0,
            confidantProviderId: item?.confidantProvider,
            confidantProviderName: item?.providerName,
            prescribingProvider: item?.provider === 'Confidant Provider' ? 'Internal' : 'External',
            providerName: item?.providerName,
            symptomsBeingTreated: item?.symptoms,
            pharmacyId: payload.pharmacy?.id,
            pharmacyName: payload.pharmacy?.name,
            pharmacyCode: payload.pharmacy?.externalId,
            pharmacyZipCode: payload.pharmacy?.zipCode,
          },
        },
      })),
    };

    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: async (errorMsg, response) => {
          if (errorMsg) {
            setIsSavingMedication(false);
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: errorMsg || 'Failed to create medication. Please try again.',
              }),
            );
            onCloseDrawer();
            return;
          }

          try {
            // Fetch the records again to get the newly created association
            const { data: updatedRecords } = await getAssignedPrioritiesV2({
              patientId: memberId,
              dataDomainType: typeName,
              pageNumber: 0,
              pageSize: 20,
              importanceLevels: getFilters(value)?.importanceLevels,
              searchQuery: '',
            });

            // Find the newly created association by matching domainElementId
            const targetDomainElementId = payload.medications[0].item.value;
            const newlyCreatedAssociation = updatedRecords.records.find(
              record => record.domainElementId === targetDomainElementId,
            );

            if (!newlyCreatedAssociation) {
              throw new Error('Could not find the newly created medication association');
            }

            setIsSavingMedication(false);

            // Use the association ID from the fetched record
            const associatedTagId = newlyCreatedAssociation.id;
            const confidantProviderId = payload.medications[0]?.confidantProvider;

            if (confidantProviderId) {
              const status = await fetchNavItemStatus({ providerId: confidantProviderId });
              const statusData = status?.data?.data;
              if (!statusData || !statusData['MDToolboxAccess']) {
                // Silently skip automation because of missing mdtoolbox credentials
                onCloseDrawer();
                return;
              }

              const { sessionId, isQueued } = await startMdToolboxAutomation(
                memberId,
                associatedTagId,
                confidantProviderId,
              );

              if (sessionId) {
                // Store sessionId and trigger automation in child component
                setAutomationSessionId(sessionId);
                setShouldStartAutomation(true);
                if (isQueued && handleQueued) {
                  handleQueued();
                }
              } else {
                console.warn('No Session id received, cannot submit medication');
                onCloseDrawer();
              }
            } else {
              // No confidantProviderId, just close the drawer normally
              onCloseDrawer();
            }
          } catch (error) {
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: error.message || 'Failed to submit the medication data. Please try again.',
              }),
            );
            // Reset automation states but keep drawer open
            onCloseDrawer(false);
          }
        },
      }),
    );
  };

  const markExternalInactive = async record => {
    setIsSavingMedication(true);
    try {
      await updateAssociatedTag(
        {
          importanceLevel: 'INACTIVE_EXTERNAL_RX',
        },
        { associatedTagId: record.id },
      );
      dispatch(
        showSnackbar({
          snackType: 'success',
          snackMessage: 'External Medication marked as Inactive.',
        }),
      );
      setDisplayedRecords(prev =>
        prev.map(item =>
          item.id === record.id ? { ...item, currentImportanceLevel: 'INACTIVE_EXTERNAL_RX' } : item,
        ),
      );
      setReset(!reset);
    } catch (e) {
      const errorMsg = e?.data?.message || e?.message;
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: errorMsg || 'Failed to mark medication as inactive. Please try again.',
        }),
      );
    } finally {
      setIsSavingMedication(false);
    }
  };

  const onDiscontinueMedication = (reason, discontinuedAt, medication) => {
    setIsSavingMedication(true);

    const rxDrugInfo = medication?.tagMetaData?.rxDrugInfo || {};
    const metaData = medication?.tagMetaData || {};

    const data = {
      patientId: memberId,
      tagItems: [
        {
          assignedBy: profile.fullName || meta.nickName,
          domainElementId: medication.domainElementId,
          metaData: {
            notes: metaData.notes,
            selectedImportance: 'DISCONTINUED',
            rxDrugInfo: {
              sig: rxDrugInfo.sig,
              externalMedicationId: rxDrugInfo.externalMedicationId,
              externalMedicationName: rxDrugInfo.externalMedicationName,
              refills: rxDrugInfo.refills,
              dispenseAmount: rxDrugInfo.dispenseAmount ? Number(rxDrugInfo.dispenseAmount) : 0,
              associatedDiagnoses: rxDrugInfo.associatedDiagnoses,
              allowSubstitutions: rxDrugInfo.allowSubstitutions,
              pharmacyNotes: rxDrugInfo.pharmacyNotes,
              additionalNotes: rxDrugInfo.additionalNotes,
              quantity: rxDrugInfo.quantity ? Number(rxDrugInfo.quantity) : 0,
              dose: rxDrugInfo.dose ?? '',
              frequency: rxDrugInfo.frequency,
              duration: rxDrugInfo.duration ? Number(rxDrugInfo.duration) : 0,
              confidantProviderId: rxDrugInfo.confidantProviderId,
              confidantProviderName: rxDrugInfo.confidantProviderName,
              prescribingProvider: rxDrugInfo.prescribingProvider,
              providerName: rxDrugInfo.providerName,
              symptomsBeingTreated: rxDrugInfo.symptomsBeingTreated,
              pharmacyId: rxDrugInfo.pharmacyId,
              pharmacyName: rxDrugInfo.pharmacyName,
              pharmacyCode: rxDrugInfo.pharmacyCode,
              pharmacyZipCode: rxDrugInfo.pharmacyZipCode,
              discontinuedReason: reason,
              discontinuedAt: discontinuedAt ? dayjs(discontinuedAt).toISOString() : null,
            },
          },
        },
      ],
    };

    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: async (errorMsg, response) => {
          if (errorMsg) {
            setIsSavingMedication(false);
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: errorMsg || 'Failed to discontinue medication. Please try again.',
              }),
            );
            setIsDiscontinueDrawerOpen(false);
            setSelectedMedicationForDiscontinue(null);
            return;
          }

          try {
            setIsSavingMedication(false);
            dispatch(
              showSnackbar({
                snackType: 'success',
                snackMessage: 'Medication discontinued successfully.',
              }),
            );
            setIsDiscontinueDrawerOpen(false);
            setSelectedMedicationForDiscontinue(null);
            setReset(!reset);
            // Fetch member details to refresh the data
            dispatch(profileActionCreators.fetchDemographicMetadata());
            dispatch(profileActionCreators.fetchDemographicDetails(memberId));
          } catch (error) {
            setIsSavingMedication(false);
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: error.message || 'Failed to discontinue medication. Please try again.',
              }),
            );
            setIsDiscontinueDrawerOpen(false);
            setSelectedMedicationForDiscontinue(null);
          }
        },
      }),
    );
  };

  const handleLoadMore = () => {
    setValue(prev => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        currentPage: prev.pagination.currentPage + 1,
      },
    }));
  };

  const handleMedicationUpdate = (record, isExternalRx) => {
    if (isExternalRx) {
      setExistingExternalRx(record);
      setShowExternalRx(true);
    } else {
      setSelectedType(FactorType.MEDICATIONS);
      setMedicationMode(MEDICATION_MODE.UPDATE);
      setExistingMedicationForDrawer(record);
    }
  };

  const handleMedicationRefill = record => {
    setSelectedType(FactorType.MEDICATIONS);
    setMedicationMode(MEDICATION_MODE.REFILL);
    setExistingMedicationForDrawer(record);
  };

  const handleMedicationDiscontinue = record => {
    setSelectedMedicationForDiscontinue(record);
    setIsDiscontinueDrawerOpen(true);
  };

  const handleMedicationView = record => {
    setSelectedItem(mapToLegacy(record, lookupData, DOMAIN_ELEMENT_VARIENT.MEDICATION));
  };

  const hasMoreRecords = displayedRecords.length < totalRecords;

  const allowModification =
    profile.designation === ProfileInfoRole.NURSE_PRACTITIONER ||
    profile.designation === ProfileInfoRole.PHYSICIAN ||
    isAdmin;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Header
          label="Medications"
          actions={
            allowModification && [
              {
                children: <Typography {...typography.body.normal.medium.bold}>New medication</Typography>,
                variant: btnType.PRIMARY,
                className: classes.primaryBtn,
                icon: 'plus',
                onClick: () => {
                  setShowMedicationSelection(true);
                  // Reset automation state when opening drawer
                  setShouldStartAutomation(false);
                  setIsAutomationLoading(false);
                  setAutomationSessionId(null);
                },
              },
            ]
          }
        />
        <Box display="flex" flexDirection="row" padding="40px" sx={sxStyles.cardBg(false)}>
          <Box flex={1} display="flex" flexDirection="column" gap="12px">
            <Box
              className="heading"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                {demographicDetails?.state} PDMP Check
              </Text>
              <Text
                level={textLevel.S}
                weight={fontWeight.BOLD}
                color={colors.primary600}
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  const patientState = demographicDetails?.state;
                  const url =
                    patientState === 'Connecticut'
                      ? 'https://connecticut.pmpaware.net/login'
                      : patientState === 'Texas'
                      ? 'https://texas.pmpaware.net/login'
                      : patientState === 'Florida'
                      ? 'https://florida.pmpaware.net/login'
                      : patientState === 'Virginia'
                      ? 'https://virginia.pmpaware.net/login'
                      : patientState === 'New Hampshire'
                      ? 'https://newhampshire.pmpaware.net/login'
                      : null;
                  if (url) {
                    window.open(url, '_blank');
                  }
                }}
              >
                Check PDMP
              </Text>
            </Box>
            <Box
              sx={{
                '& strong': {
                  fontSize: '14px',
                },
                '& p': {
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 500,
                  color: colors.neutral600,
                },
                '& ul': {
                  margin: 0,
                },
              }}
            >
              {demographicDetails?.pdmpSummary ? (
                <Markdown>{demographicDetails.pdmpSummary}</Markdown>
              ) : (
                <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral600}>
                  No PDMP summary available
                </Text>
              )}
            </Box>
            <Box>
              {demographicDetails?.pdmpLastUpdated && (
                <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral600}>
                  Checked on {dayjs(demographicDetails?.pdmpLastUpdated).format('MM/DD/YY')} at{' '}
                  {dayjs(demographicDetails?.pdmpLastUpdated).format('h:mma')}
                </Text>
              )}
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gap="16px" flex={1}>
            <Box
              bgcolor={colors.primary50}
              borderRadius="8px"
              display="flex"
              flexDirection="column"
              padding="24px"
              gap="16px"
            >
              <Box
                className="heading"
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Text level={textLevel.S} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                  Preferred pharmacy
                </Text>
                <Text
                  level={textLevel.S}
                  weight={fontWeight.BOLD}
                  color={colors.primary600}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setIsEditPharmacyDrawerOpen(true)}
                >
                  {allowModification ? 'Edit' : ''}
                </Text>
              </Box>
              {!demographicDetails?.preferredPharmacy ? (
                <Box display="flex" justifyContent="center" alignItems="center" padding="20px">
                  <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral600}>
                    No preferred pharmacy
                  </Text>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap="8px">
                  <Box display="flex" flexDirection="row" alignItems="center" gap="8px">
                    <Icons glyph="pharmacy-filled" color={colors.primary500} className={classes.icon} />
                    <Text level={textLevel.S} weight={fontWeight.BOLD} color={colors.neutral700}>
                      {isLoadingPharmacy
                        ? 'Loading...'
                        : pharmacyDetails?.name || demographicDetails?.preferredPharmacyName || 'N/A'}
                    </Text>
                  </Box>

                  <Box display="flex" flexDirection="row" alignItems="center" gap="8px">
                    <Icons glyph="location" color={colors.primary500} className={classes.icon} />
                    <Box display="flex" flexDirection="column">
                      <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                        {isLoadingPharmacy ? 'Loading...' : pharmacyDetails?.address1 || 'N/A'}
                      </Text>
                      {pharmacyDetails?.address2 && (
                        <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                          {pharmacyDetails.address2}
                        </Text>
                      )}
                    </Box>
                  </Box>

                  <Box display="flex" flexDirection="row" alignItems="center" gap="8px">
                    <Icons glyph="phone" color={colors.primary500} className={classes.icon} />
                    <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                      {isLoadingPharmacy
                        ? 'Loading...'
                        : pharmacyDetails?.phone
                        ? `(${pharmacyDetails.phone.slice(0, 3)}) ${pharmacyDetails.phone.slice(
                            3,
                            6,
                          )}-${pharmacyDetails.phone.slice(6)}`
                        : 'N/A'}
                    </Text>
                  </Box>

                  <Box display="flex" flexDirection="row" alignItems="center" gap="8px">
                    <Icons glyph="info" color={colors.primary500} className={classes.icon} />
                    <Box display="flex" flexDirection="column">
                      <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                        {pharmacyDetails?.open24hrs ? 'Open 24 hours' : 'Not open 24 hours'}
                      </Text>
                      <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                        {pharmacyDetails?.controlledSubstances
                          ? 'Controlled substances available'
                          : 'Controlled substances not available'}
                      </Text>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        {flattenedRecords && flattenedRecords.length > 0 ? (
          sortMedicationsWithExternalLast(flattenedRecords).map((record, index) => {
            const isExternalRx = isExternalRxRecord(record);
            return (
              <MedicationCard
                key={record.id || index}
                medication={record}
                preferredPharmacyName={demographicDetails?.preferredPharmacyName}
                onClick={isExternalRx ? undefined : () => handleMedicationView(record)}
                onUpdateClick={() => handleMedicationUpdate(record, isExternalRx)}
                onRefillClick={() => handleMedicationRefill(record)}
                onDiscontinueClick={() => handleMedicationDiscontinue(record)}
                onMarkInactive={() => markExternalInactive(record)}
                isSavingMedication={isSavingMedication}
                sx={sxStyles.cardBg(true)}
              />
            );
          })
        ) : (
          <Box
            sx={{
              width: '100%',
              '& > div': {
                minHeight: 'auto !important',
                padding: '100px 56px',
              },
            }}
          >
            <EmptyState
              title="No medications yet"
              subtitle={`No medications have been prescribed for ${
                demographicDetails?.nickName || 'this member'
              } yet.`}
              glyph="lottie-medications"
              glyphOrientation="landscape"
              isEmptyWithLottie
              lottieSize={128}
            />
          </Box>
        )}

        {hasMoreRecords && (
          <Box display="flex" justifyContent="center" paddingTop={2}>
            <Button
              variant={btnType.TEXT}
              size={btnSize.MEDIUM}
              onClick={handleLoadMore}
              disabled={isLoading}
            >
              <Typography {...typography.body.normal.medium.bold}>
                {isLoading ? 'Loading...' : 'Load more'}
              </Typography>
            </Button>
          </Box>
        )}
      </Box>

      <AddFactorDrawer
        open={showMedicationSelection}
        onClose={() => setShowMedicationSelection(false)}
        title="Add Medication"
        variant={DrawerType.MEDICATION}
        onChangeSelect={selected => {
          setShowMedicationSelection(false);
          if (selected === FactorType.SEND_RX) {
            setSelectedType(FactorType.MEDICATIONS);
          } else if (selected === FactorType.EXTERNAL_RX) {
            setShowExternalRx(true);
          }
        }}
      />
      <AddNewReportMedications
        isOpen={selectedType === FactorType.MEDICATIONS}
        onClose={() => {
          setMedicationMode(MEDICATION_MODE.ADD);
          setExistingMedicationForDrawer(null);
          onCloseDrawer();
        }}
        options={domainOptions}
        lookupData={lookupData}
        onSubmit={onSubmitNewReport}
        optionsLoading={optionsLoading}
        shouldStartAutomation={shouldStartAutomation}
        setShouldStartAutomation={setShouldStartAutomation}
        isAutomationLoading={isAutomationLoading}
        automationSessionId={automationSessionId}
        onAutomationSuccess={() => {
          setAutomationSessionId(null);
          setShouldStartAutomation(false);
          setIsAutomationLoading(false);
          setIsSavingMedication(false);
        }}
        isSaving={isSavingMedication}
        onAutomationError={onAutomationError}
        mode={medicationMode}
        existingMedication={existingMedicationForDrawer}
      />
      <AddExternalRxDrawer
        open={showExternalRx}
        onClose={() => {
          setShowExternalRx(false);
          setExistingExternalRx(null);
          setReset(!reset);
        }}
        memberId={memberId}
        otherDomainElementId={otherDomainElementId}
        existingRecord={existingExternalRx}
      />
      {selectedItem?.taxonomyName && (
        <PriorityDetailDrawer
          memberId={memberId}
          selectedItem={selectedItem}
          type="medications"
          onClose={forceRefresh => {
            setSelectedItem(null);
            setSelectedType(null);
            onCloseDrawer(forceRefresh);
          }}
          open={selectedItem?.taxonomyName}
          onElementsUpdated={() => setReset(!reset)}
        />
      )}
      <DiscontinueRXDrawer
        open={isDiscontinueDrawerOpen}
        onClose={() => {
          setIsDiscontinueDrawerOpen(false);
          setSelectedMedicationForDiscontinue(null);
        }}
        onContinue={(reason, discontinuedAt, medication) => {
          onDiscontinueMedication(reason, discontinuedAt, medication);
        }}
        medication={selectedMedicationForDiscontinue}
      />
      <EditPharmacyDrawer
        open={isEditPharmacyDrawerOpen}
        onClose={() => {
          setIsEditPharmacyDrawerOpen(false);
        }}
        selectedPharmacy={pharmacyDetails}
        demographicDetails={demographicDetails}
        memberId={memberId}
      />
    </>
  );
};

export default Medications;
